import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import AceEditor from "react-ace";
import { isPlainObject, isArray } from "lodash";
import Modal from "../../../../components/organisms/Modal";
import { AutoMapperNode } from "../../../../components/molecules";
import "reactflow/dist/style.css";
import ReactFlow, {
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "reactflow";
import {
  MODAL_SIZE,
  MODAL_POSITION,
  ACE_EDITOR_THEMES,
  ZERO,
  DATA_TYPES,
  AUTO_MAPPER_OPERATIONS,
  EMPTY_ARRAY,
} from "../../../../constants";
import { setAutoMapper } from "../../../DataModels";
import { selectAutoMapper } from "../../dataModels.selectors";
import { MODALS } from "../../../../components/organisms/AppModals/modals.constants";
import { actions as modalActions } from "../../../../components/organisms/Modal/modal.reducers";
import { Alert } from "../../../../components/molecules";
import { getCollectionProperties } from "../../Expression.helpers";
import {
  getFormattedCode,
  convertDictToList,
  findObjectInNestedArray,
} from "../../../../helpers";

const CANCEL = "Cancel";
const SUBMIT = "Submit";
const VARIANT = "lines";
const GAP_VALUE = 7;
const EMPTY_STRING = "";
const REMOVE = "remove";
const NEXT = "Next";
const MOVE = "move";
const INVALID_JSON_MESSAGE = "Invalid JSON document";
const HANDLE_TYPE_ALERT_MESSAGE = "Handle type must be same";
const THREE_SECONDS = 3000;
const {
  POPULATE_MAPPER,
  SET_RESPONSE_EXPANDED_KEYS,
  SET_SOURCE_EXPANDED_KEYS,
  SHOULD_SHOW_RESPONSE_EDITOR,
  SHOULD_SHOW_SOURCE_EDITOR,
  RESET_AUTO_MAPPER,
} = AUTO_MAPPER_OPERATIONS;
const { FULL, MD } = MODAL_SIZE;
const { CENTER } = MODAL_POSITION;
const { setSelectedModal } = modalActions;
const { DARK } = ACE_EDITOR_THEMES;
const { JSON_MODE } = DATA_TYPES;
const {
  ADD,
  DELETE,
  MODIFY_SOURCE,
  MODIFY_RESPONSE,
  NODES,
  EDGES,
  RESET_MAPPER,
} = AUTO_MAPPER_OPERATIONS;

const nodeTypes = {
  source: (props) => (
    <AutoMapperNode target="source" title={"Source Object"} {...props} />
  ),
  response: (props) => (
    <AutoMapperNode target="target" title={"Response Object"} {...props} />
  ),
};

const initialNodes = [
  {
    id: "sourceNode",
    type: "source",
    position: { x: 650, y: 5 },
    data: {},
    draggable: true,
  },
  {
    id: "responseNode",
    type: "response",
    data: {},
    position: { x: 1350, y: 5 },
    draggable: true,
  },
];

const findParentNode = (currentNode, allNodes, allEdges) => {
  try {
    const connectedEdge = allEdges.find(
      (item) => item.target === currentNode.id
    );
    const parentNodeId = connectedEdge.source;
    return allNodes.find((item) => item.id === parentNodeId);
  } catch (error) {
    return -1;
  }
};

const isValidJson = (data) =>
  isPlainObject(JSON.parse(data)) || isArray(JSON.parse(data));

const MapperModal = ({
  name,
  value,
  handleChange,
  title,
  allNodes,
  allEdges,
  currentNode,
  collections,
}) => {
  const dispatch = useDispatch();
  const autoMapper = useSelector(selectAutoMapper);
  const {
    shouldShowResponseEditor,
    shouldShowSourceEditor,
    sourceObject,
    responseObject,
  } = autoMapper;
  const convertedSource = convertDictToList(sourceObject);
  const responseSource = convertDictToList(responseObject);
  const [nodes] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState(EMPTY_ARRAY);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [isSourceObjectValid, setIsSourceObjectValid] = useState(true);
  const [isResponseObjectValid, setIsResponseObjectValid] = useState(true);
  const [reactFlowInstance, setReactFlowInstance] = useState();
  const [sourceInputData, setSourceInputData] = useState(EMPTY_STRING);
  const [responseInputData, setInputMapperData] = useState(EMPTY_STRING);
  const [sourceParsedJson, setSourceParsedJson] = useState({});
  const [responseParsedJson, setResponseParsedJson] = useState({});
  const [shouldShowAlert, setShouldShowAlert] = useState(false);
  const [shouldShowAceEditor, setShouldShowAceEditor] = useState();
  const label =
    Object.keys(value).length > ZERO ? `Edit ${title}` : `Configure ${title}`;
  const proOptions = { hideAttribution: true };

  useEffect(() => {
    setShouldShowAceEditor(shouldShowResponseEditor || shouldShowSourceEditor);
  }, [shouldShowResponseEditor, shouldShowSourceEditor]);

  useEffect(() => {
    reactFlowInstance?.fitView();
  }, [reactFlowInstance]);

  useEffect(() => {
    try {
      const isValidJsonData = isValidJson(sourceInputData);
      setIsSourceObjectValid(isValidJsonData);
      if (isValidJsonData) setSourceParsedJson(JSON.parse(sourceInputData));
    } catch (e) {
      setIsSourceObjectValid(false);
    }
  }, [sourceInputData, shouldShowSourceEditor, edges]);

  useEffect(() => {
    try {
      const isValidJsonData = isValidJson(responseInputData);
      setIsResponseObjectValid(isValidJsonData);
      if (isValidJsonData) setResponseParsedJson(JSON.parse(responseInputData));
    } catch (e) {
      setIsResponseObjectValid(false);
    }
  }, [responseInputData, shouldShowResponseEditor, edges]);

  useEffect(() => {
    dispatch(setAutoMapper({ edges, operation: EDGES }));
  }, [edges, dispatch]);

  useEffect(() => {
    if (Object.keys(value).length) {
      setEdges(value.edges);
      dispatch(setAutoMapper({ edges: value.edges, operation: EDGES }));
      setSourceInputData(
        getFormattedCode(JSON_MODE, JSON.stringify(value.sourceObject))
      );
      setInputMapperData(
        getFormattedCode(JSON_MODE, JSON.stringify(value.responseObject))
      );
      dispatch(
        setAutoMapper({
          response: value.responseObject,
          operation: MODIFY_RESPONSE,
        })
      );
      dispatch(
        setAutoMapper({ source: value.sourceObject, operation: MODIFY_SOURCE })
      );

      dispatch(
        setAutoMapper({
          mapper: value.mapperObject,
          operation: POPULATE_MAPPER,
        })
      );

      dispatch(
        setAutoMapper({
          sourceExpandedKeys: value.expandedKeys.source,
          operation: SET_SOURCE_EXPANDED_KEYS,
        })
      );
      dispatch(
        setAutoMapper({
          responseExpandedKeys: value.expandedKeys.response,
          operation: SET_RESPONSE_EXPANDED_KEYS,
        })
      );
    } else
      dispatch(
        setAutoMapper({
          operation: RESET_AUTO_MAPPER,
        })
      );
  }, [setEdges, value, dispatch]);

  const updateMapperObject = useCallback(
    (params) => {
      const key = params.sourceHandle;
      const value = params.targetHandle;
      dispatch(setAutoMapper({ nodes, operation: NODES }));
      dispatch(setAutoMapper({ edges, operation: EDGES }));
      dispatch(setAutoMapper({ key, value, operation: ADD }));
    },
    [dispatch, nodes, edges]
  );

  const onConnect = useCallback(
    (params) => {
      const updatedParams = {
        ...params,
        markerEnd: {
          type: MarkerType.ArrowClosed,
        },
      };
      const sourceHandles = edges.map((each) => each.sourceHandle);
      const targetHandles = edges.map((each) => each.targetHandle);

      const shouldCreateEdge =
        !sourceHandles.includes(params.sourceHandle) &&
        !targetHandles.includes(params.targetHandle);

      const targetObjectSour = { level: params.sourceHandle };
      const targetObjectRes = { level: params.targetHandle };

      const src = findObjectInNestedArray(convertedSource, targetObjectSour);
      const res = findObjectInNestedArray(responseSource, targetObjectRes);

      if (shouldCreateEdge && src.type === res.type) {
        setEdges((eds) => addEdge(updatedParams, eds));
        updateMapperObject(updatedParams);
      } else setShouldShowAlert(true);
    },
    [edges, setEdges, updateMapperObject, convertedSource, responseSource]
  );

  const handleOpenModal = () => {
    setShouldShowModal(true);
    dispatch(setSelectedModal(MODALS.MAPPER_MODAL));
  };

  const handleCloseAceEditor = useCallback(() => {
    setShouldShowAceEditor(false);
    dispatch(
      setAutoMapper({
        shouldShowModal: false,
        operation: SHOULD_SHOW_SOURCE_EDITOR,
      })
    );
    dispatch(
      setAutoMapper({
        shouldShowModal: false,
        operation: SHOULD_SHOW_RESPONSE_EDITOR,
      })
    );
  }, [dispatch]);

  const handleClose = useCallback(() => {
    setShouldShowModal(false);
    dispatch(setSelectedModal(null));
  }, [dispatch]);

  const handleOnSubmit = () => {
    handleChange(autoMapper, name);
    setShouldShowModal(false);
    dispatch(
      setAutoMapper({
        operation: RESET_AUTO_MAPPER,
      })
    );
  };

  const onClickNext = () => {
    if (shouldShowSourceEditor) {
      dispatch(
        setAutoMapper({
          shouldShowModal: false,
          operation: SHOULD_SHOW_SOURCE_EDITOR,
        })
      );
      dispatch(
        setAutoMapper({
          source: sourceParsedJson,
          operation: MODIFY_SOURCE,
        })
      );
    } else if (shouldShowResponseEditor) {
      dispatch(
        setAutoMapper({
          shouldShowModal: false,
          operation: SHOULD_SHOW_RESPONSE_EDITOR,
        })
      );
      dispatch(
        setAutoMapper({
          response: responseParsedJson,
          operation: MODIFY_RESPONSE,
        })
      );
    }
    dispatch(setAutoMapper({ edges: [], operation: EDGES }));
    dispatch(setAutoMapper({ operation: RESET_MAPPER }));
    setEdges([]);
  };

  const footer = (
    <div className="p-6 border-t border-gray-700 text-right">
      {!shouldShowAceEditor ? (
        <>
          <button
            className="btn btn-primary-light mr-1"
            type="button"
            onClick={handleClose}
          >
            {CANCEL}
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            onClick={handleOnSubmit}
          >
            {SUBMIT}
          </button>
        </>
      ) : (
        <button
          className="btn btn-primary-light mr-1"
          type="button"
          onClick={onClickNext}
        >
          {NEXT}
        </button>
      )}
    </div>
  );

  const onEdgesChange = (event) => {
    if (event[ZERO].type === REMOVE) {
      const edge = edges.find((each) => each.id === event[ZERO].id);
      dispatch(setAutoMapper({ key: edge.sourceHandle, operation: DELETE }));
      const filteredEdges = edges.filter((each) => each.id !== edge.id);
      setEdges(filteredEdges);
    }
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = MOVE;
  }, []);

  useEffect(() => {
    if (shouldShowAlert) {
      const timeoutId = setTimeout(() => {
        setShouldShowAlert(false);
      }, THREE_SECONDS);

      return () => clearTimeout(timeoutId);
    }
  }, [shouldShowAlert]);

  useEffect(() => {
    try {
      const parentNode = findParentNode(currentNode, allNodes, allEdges);
      if (parentNode === -1) return;
      const nodeConfigs = parentNode.data.config;
      const modelName = nodeConfigs.find(
        (item) => item.key === "modelName"
      )?.defaultValue;
      if (!modelName) return;
      const properties = getCollectionProperties(modelName, collections);
      const formattedProperties = properties.reduce((obj, key) => {
        obj[key] = EMPTY_STRING;
        return obj;
      }, {});
      setSourceInputData(
        getFormattedCode(JSON_MODE, JSON.stringify(formattedProperties))
      );
      dispatch(
        setAutoMapper({ source: formattedProperties, operation: MODIFY_SOURCE })
      );
    } catch (error) {}
  }, [dispatch, allNodes, allEdges, currentNode, collections]);

  return (
    <>
      <button
        className="btn btn-primary h-auto w-full"
        type="button"
        onClick={handleOpenModal}
      >
        {label}
      </button>
      {shouldShowModal && (
        <Modal
          size={shouldShowAceEditor ? MD : FULL}
          title={label}
          footer={footer}
          position={CENTER}
          handleClose={shouldShowAceEditor ? handleCloseAceEditor : handleClose}
        >
          {shouldShowAceEditor ? (
            <div className="flex-row">
              <div>
                <AceEditor
                  mode="json"
                  theme={DARK}
                  name="aceEditor"
                  className="ace-editor max-w-full"
                  height="430px"
                  width="800px"
                  defaultValue={EMPTY_STRING}
                  value={
                    shouldShowSourceEditor ? sourceInputData : responseInputData
                  }
                  focus={true}
                  onChange={
                    shouldShowSourceEditor
                      ? setSourceInputData
                      : setInputMapperData
                  }
                  setOptions={{
                    showGutter: true,
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: true,
                    fontSize: 14,
                    highlightActiveLine: true,
                    tabSize: 4,
                    useWorker: false,
                  }}
                />
                {((sourceInputData && !isSourceObjectValid) ||
                  (responseInputData && !isResponseObjectValid)) && (
                  <div className="absolute text-red-500">
                    {INVALID_JSON_MESSAGE}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {shouldShowAlert && (
                <Alert
                  message={HANDLE_TYPE_ALERT_MESSAGE}
                  handleOnClose={() => setShouldShowAlert(false)}
                />
              )}
              <div className="overflow-y-scroll no-scrollbar flex-auto bg-white border border-gray-700">
                <div className="relative h-full w-full">
                  <ReactFlow
                    fitView
                    nodes={nodes}
                    nodeTypes={nodeTypes}
                    onConnect={onConnect}
                    edges={edges}
                    onEdgesChange={onEdgesChange}
                    proOptions={proOptions}
                    onInit={(ref) => setReactFlowInstance(ref)}
                    onDragOver={onDragOver}
                  >
                    <Background variant={VARIANT} gap={GAP_VALUE} />
                    <Controls />
                  </ReactFlow>
                </div>
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default MapperModal;
